/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
    afterNextRender,
    AfterViewChecked,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    OnInit,
    signal,
    ViewChild
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EditableGridComponent} from '../editable-grid-component';
import {refreshCellAndDeferredRowRedraw} from '../cell-renderer/prime-cell-editor-tab.util';
import {CellEditingStoppedEvent, CellValueChangedEvent, ColDef, GridReadyEvent} from 'ag-grid-community';
import {AgGridAngular} from 'ag-grid-angular';
import {
    CreateIcdsFromTemplatesResponse,
    formatIoMatrixScopeLabel,
    IcdTemplateInventory,
    IoMatrixLoadResponse,
    IoMatrixRow,
    IoMatrixView,
    normalizeIoMatrixScopeField,
    POWERTRAIN_IO_MATRIX_DISPLAY_NAME,
    resolveIoMatrixDeviceNameBanner
} from '../../model/io-matrix.models';
import {catchError, finalize, forkJoin, map, Observable, of, take, timeout} from 'rxjs';
import {SearchUser} from '../../shared/model/user';
import {CommonHelperService} from '../../service/common-helper.service';
import {
    buildTemplateColDef,
    buildTemplateField,
    IcdTemplateColumn,
    isTemplateField,
    normalizeTemplateCircuitSelection,
    TEMPLATE_PIN_EDITOR_OPTIONS
} from './icd-template-columns';
import {environment} from '../../../environments/environment';
import {ValidationModel} from '../../model/validation.model';
import {
    buildDeviceIoDiffRows,
    CadenceMergeRowMode,
    getIoMatrixMergeCellTooltip,
    POWERTRAIN_DEVICE_IO_DIFF_FIELDS,
    renderCadenceMergeCellContent,
    wireDefaultIoMatrixMergeTooltips
} from '../../utils/cadence-merge.util';
import {buildMergeCellElement} from '../../utils/merge-cell-renderer.util';

type ObjectCodeInstOption = {
  label: string;
  value: string;
};

type CadenceMergeMatchField = {
  path: string;
  candidateMatch: boolean;
};

/**
 * The Object Code Inst field's label. One string in five places - the signal's initial value, the template
 * inventory column header, and the three points that reset the label after it was switched to
 * CONTROLLER_DESIGNATOR mode - so it lives here rather than being retyped at each of them.
 */
const OBJECT_CODE_INST_LABEL = 'Object Code Inst';

/** Local calendar day as yyyy-MM-dd for {@code <input type="date">}. Do not use toISOString (UTC). */
function todayAsIsoDate(): string {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
}

@Component({
  selector: 'app-io-matrix-workflow',
  template: '',
  standalone: false
})
export class IoMatrixWorkflowComponent extends EditableGridComponent implements OnInit, AfterViewChecked {
  private static readonly IO_MATRIX_FIELD_RULES = structuredClone(new IoMatrixRow().fieldRules);
  @ViewChild(AgGridAngular, {read: ElementRef}) protected agGridHost?: ElementRef<HTMLElement>;

  // `EditableGridComponent`'s `editable` callback treats a missing `adminPermissionUrl` as
  // "no separate admin gate needed", which is the intended ICD behavior here. Setting an
  // identical `adminPermissionUrl = '/esciUI/icdPermission'` would just double the network
  // call without changing access semantics, so it is intentionally omitted.
  permissionUrl = '/esciUI/icdPermission';
  saveUrl = '/esciUI/ioMatrix/save';
  private downloadActionsContainer: HTMLElement | null = null;
  private downloadReportButton: HTMLButtonElement | null = null;
  private downloadHistoryButton: HTMLButtonElement | null = null;
  /** Default page size; must be one of {@link paginationPageSizeSelector} for AG Grid selector to match. */
  paginationPageSize = 1000;
  paginationPageSizeSelector: number[] = [100, 500, 1000];
  private readonly objectCodeSignal = signal('');
  private readonly objectCodeQuerySignal = signal('');
  private readonly objectCodeInstSignal = signal('');
  private readonly objectCodeInstQuerySignal = signal('');
  private readonly objectCodeInstLabelSignal = signal(OBJECT_CODE_INST_LABEL);
  private readonly objectCodeInstModeSignal = signal<'OCI' | 'CONTROLLER_DESIGNATOR'>('OCI');
  private readonly objectCodeInstSelectedSignal = signal(false);
  private readonly objectCodeInstOptionsSignal = signal<ObjectCodeInstOption[]>([]);
  private readonly architectureSignal = signal('');
  private readonly cadenceVerSignal = signal('');
  private readonly selectedProgramSignal = signal('');
  private readonly selectedModelYearSignal = signal('');
  private readonly outdatedCadenceSignal = signal(false);
  private readonly matrixInUseSignal = signal(false);
  private readonly objectCodesSignal = signal<string[]>([]);
  private readonly architecturesSignal = signal<string[]>([]);
  private readonly circuitOptionsSignal = signal<string[]>(['X']);
  private readonly ioMatrixRowsSignal = signal<IoMatrixRow[]>([]);
  private readonly templateRowsSignal = signal<IoMatrixRow[]>([]);
  private readonly templateInventorySignal = signal<IcdTemplateInventory[]>([]);
  /**
   * Whether the template inventory request is still out. templateInventory itself cannot say - its setter
   * coerces null to [], so "loading" and "loaded but empty" are the same value - and telling those apart is
   * what lets a grid show a loading overlay instead of "no rows".
   */
  private readonly templateInventoryLoadingSignal = signal(false);
  private readonly selectedTemplateIdsSignal = signal<number[]>([]);
  protected readonly ioMatrixCreatedSignal = signal(false);
  private readonly icdCreationStartedSignal = signal(false);
  private readonly createIcdSubmissionInProgressSignal = signal(false);
  private readonly deviceIoCadenceVerSignal = signal('');
  private readonly latestSavedCadenceVerSignal = signal('');
  private readonly cadenceUpgradeAvailableSignal = signal(false);
  private lastCommittedObjectCode = '';
  private lastCommittedObjectCodeInst = '';
  private objectCodeInstsRequestId = 0;
  protected readonly destroyRef = inject(DestroyRef);
  protected cadenceMergePreviewActive = false;
  protected cadenceMergeSourceCadenceVer = '';
  protected deviceIoSyncPreviewActive = false;
  protected deviceIoSyncSummary = '';
  protected syncingDeviceIo = false;
  protected inlineMergeOriginalValueDisplay = false;
  private deviceIoDriftSummary: IoMatrixLoadResponse['driftSummary'] | null = null;
  private lastLoadedSavedRows: IoMatrixRow[] = [];
  private lastLoadedLiveRows: IoMatrixRow[] = [];
  private readonly refMapWarningSignal = signal(false);
  readonly matrixId = computed(() => {
    const objectCode = this.objectCodeSignal();
    const objectCodeInst = normalizeIoMatrixScopeField(this.objectCodeInstSignal());
    const hasObjectCodeInstSelection = this.objectCodeInstSelectedSignal();
    const architecture = this.architectureSignal();
    return objectCode && hasObjectCodeInstSelection && architecture ? `${objectCode}-${objectCodeInst}-${architecture}` : '';
  });
  /**
   * Human-readable label for the IO Matrix the user is currently editing, derived
   * from the live context signals. Empty string when no matrix is selected (no
   * matrixId or no rows loaded), so the banner stays hidden in that case.
   * Format matches the saved-matrix dropdown:
   *   `${objectCode} | ${objectCodeInst} | ${architecture} | ${cadenceVer}`
   */
  readonly editingMatrixLabel = computed(() => {
    const matrixId = this.matrixId();
    const rowCount = this.ioMatrixRowsSignal().length;
    if (!matrixId || rowCount < 1) {
      return '';
    }
    const objectCode = this.objectCodeSignal();
    const objectCodeInst = normalizeIoMatrixScopeField(this.objectCodeInstSignal());
    const architecture = this.architectureSignal();
    const cadenceVer = this.cadenceVerSignal();
    return formatIoMatrixScopeLabel(objectCode, objectCodeInst, architecture, cadenceVer);
  });
  readonly ioMatrixDeviceNameBanner = computed(() =>
    resolveIoMatrixDeviceNameBanner(this.ioMatrixRowsSignal(), !!this.matrixId()));
  readonly ioMatrixDeviceNameLabel = computed(() => this.ioMatrixDeviceNameBanner().display);
  readonly ioMatrixDeviceNameTooltip = computed(() => this.ioMatrixDeviceNameBanner().tooltip);
  readonly canDownloadCurrentMatrix = computed(() => this.ioMatrixRowsSignal().length > 0);

  get deviceIoDriftDetected(): boolean {
    // Sync compares live Device IO at the viewed cadence. After an in-place jump, older
    // snapshots have an empty live set and look wiped; Sync is not the action there.
    return !!this.deviceIoDriftSummary?.driftDetected
      && !this.cadenceUpgradeAvailable
      && !this.viewingHistoricalSavedCadence()
      && this.viewedCadenceMatchesLiveDeviceIo();
  }

  private viewingHistoricalSavedCadence(): boolean {
    const viewed = (this.cadenceVer || '').trim();
    const latestSaved = (this.latestSavedCadenceVer || '').trim();
    return !!viewed && !!latestSaved && viewed !== latestSaved;
  }

  private viewedCadenceMatchesLiveDeviceIo(): boolean {
    const viewed = (this.cadenceVer || '').trim();
    const live = (this.deviceIoCadenceVer || '').trim();
    return !!viewed && !!live && viewed === live;
  }

  get anyMergePreviewActive(): boolean {
    return this.cadenceMergePreviewActive || this.deviceIoSyncPreviewActive;
  }

  get canStartDeviceIoSync(): boolean {
    return this.deviceIoDriftDetected && !this.anyMergePreviewActive
      && !this.saving && !this.syncingDeviceIo && !this.exporting && !this.resetting;
  }

  get canChangeIoMatrixContext(): boolean {
    return !this.dataChanged && !this.anyMergePreviewActive
      && !this.saving && !this.exporting && !this.resetting;
  }
  readonly canCreateIoMatrix = computed(() =>
    !!this.objectCodeSignal() && this.objectCodeInstSelectedSignal() && !!this.architectureSignal()
  );
  readonly canSaveIoMatrix = computed(() => this.ioMatrixCreatedSignal() && this.ioMatrixRowsSignal().length > 0);

  get objectCode(): string {
    return this.objectCodeSignal();
  }

  set objectCode(value: string) {
    this.objectCodeSignal.set(value ?? '');
  }

  get objectCodeInst(): string {
    return this.objectCodeInstSignal();
  }

  set objectCodeInst(value: string) {
    this.objectCodeInstSignal.set(value ?? '');
  }

  get objectCodeInstLabel(): string {
    return this.objectCodeInstLabelSignal();
  }

  get objectCodeInstMode(): 'OCI' | 'CONTROLLER_DESIGNATOR' {
    return this.objectCodeInstModeSignal();
  }

  get refMapWarningText(): string {
    return `No Ref Mapping entry exists for selected Object Code and ${this.objectCodeInstLabel}. Save is still allowed, but ICD creation will be blocked until mapping exists.`;
  }

  get objectCodeInstOptions(): ObjectCodeInstOption[] {
    return this.objectCodeInstOptionsSignal();
  }

  set objectCodeInstOptions(value: ObjectCodeInstOption[]) {
    this.objectCodeInstOptionsSignal.set(value ?? []);
  }

  get hasObjectCodeInstSelection(): boolean {
    return this.objectCodeInstSelectedSignal();
  }

  get filteredObjectCodeSuggestions(): string[] {
    const query = (this.objectCodeQuerySignal() || '').trim().toLowerCase();
    const allCodes = this.objectCodesSignal();
    if (!query) {
      return [];
    }
    return allCodes.filter(code => (code || '').toLowerCase().includes(query));
  }

  get filteredObjectCodeInstSuggestions(): string[] {
    const query = (this.objectCodeInstQuerySignal() || '').trim().toLowerCase();
    const allInsts = this.objectCodeInstOptionsSignal().map(option => option.value);
    if (!query) {
      return allInsts;
    }
    return allInsts.filter(inst => (inst || '').toLowerCase().includes(query));
  }

  get architecture(): string {
    return this.architectureSignal();
  }

  set architecture(value: string) {
    this.architectureSignal.set(value ?? '');
  }

  get cadenceVer(): string {
    return this.cadenceVerSignal();
  }

  set cadenceVer(value: string) {
    this.cadenceVerSignal.set(value ?? '');
  }

  get selectedProgram(): string {
    return this.selectedProgramSignal();
  }

  set selectedProgram(value: string) {
    this.selectedProgramSignal.set(value ?? '');
  }

  get selectedModelYear(): string {
    return this.selectedModelYearSignal();
  }

  set selectedModelYear(value: string) {
    this.selectedModelYearSignal.set(value ?? '');
  }

  get outdatedCadence(): boolean {
    return this.outdatedCadenceSignal();
  }

  set outdatedCadence(value: boolean) {
    this.outdatedCadenceSignal.set(!!value);
  }

  get matrixInUse(): boolean {
    return this.matrixInUseSignal();
  }

  set matrixInUse(value: boolean) {
    this.matrixInUseSignal.set(!!value);
  }

  get objectCodes(): string[] {
    return this.objectCodesSignal();
  }

  set objectCodes(value: string[]) {
    this.objectCodesSignal.set(value ?? []);
  }

  get architectures(): string[] {
    return this.architecturesSignal();
  }

  set architectures(value: string[]) {
    this.architecturesSignal.set(value ?? []);
  }

  get circuitOptions(): string[] {
    return this.circuitOptionsSignal();
  }

  set circuitOptions(value: string[]) {
    this.circuitOptionsSignal.set(value ?? ['X']);
  }

  get ioMatrixRows(): IoMatrixRow[] {
    return this.ioMatrixRowsSignal();
  }

  set ioMatrixRows(value: IoMatrixRow[]) {
    this.ioMatrixRowsSignal.set(value ?? []);
  }

  get templateRows(): IoMatrixRow[] {
    return this.templateRowsSignal();
  }

  set templateRows(value: IoMatrixRow[]) {
    this.templateRowsSignal.set(value ?? []);
  }

  get templateInventory(): IcdTemplateInventory[] {
    return this.templateInventorySignal();
  }

  set templateInventory(value: IcdTemplateInventory[]) {
    this.templateInventorySignal.set(value ?? []);
  }

  /** @see templateInventoryLoadingSignal */
  get templateInventoryLoading(): boolean {
    return this.templateInventoryLoadingSignal();
  }

  get selectedTemplateIds(): number[] {
    return this.selectedTemplateIdsSignal();
  }

  set selectedTemplateIds(value: number[]) {
    this.selectedTemplateIdsSignal.set(value ?? []);
  }

  get icdCreationStarted(): boolean {
    return this.icdCreationStartedSignal();
  }

  set icdCreationStarted(value: boolean) {
    this.icdCreationStartedSignal.set(!!value);
  }

  get createIcdSubmissionInProgress(): boolean {
    return this.createIcdSubmissionInProgressSignal();
  }

  set createIcdSubmissionInProgress(value: boolean) {
    this.createIcdSubmissionInProgressSignal.set(!!value);
  }

  get deviceIoCadenceVer(): string {
    return this.deviceIoCadenceVerSignal();
  }

  set deviceIoCadenceVer(value: string) {
    this.deviceIoCadenceVerSignal.set(value ?? '');
  }

  get latestSavedCadenceVer(): string {
    return this.latestSavedCadenceVerSignal();
  }

  set latestSavedCadenceVer(value: string) {
    this.latestSavedCadenceVerSignal.set(value ?? '');
  }

  get cadenceUpgradeAvailable(): boolean {
    return this.cadenceUpgradeAvailableSignal();
  }

  set cadenceUpgradeAvailable(value: boolean) {
    this.cadenceUpgradeAvailableSignal.set(!!value);
  }

  get refMapWarning(): boolean {
    return this.refMapWarningSignal();
  }

  set refMapWarning(value: boolean) {
    this.refMapWarningSignal.set(!!value);
  }

  templateColumns: IcdTemplateColumn[] = [];

  protected readonly selectedViewSignal = signal<IoMatrixView>('architecture');
  protected sessionVariantTokens: string[] = [];
  protected readonly ioMatrixViewOptions: { label: string; value: IoMatrixView }[] = [
    {label: 'Architecture', value: 'architecture'},
    {label: 'VIOS', value: 'vios'},
    {label: 'Hardware', value: 'hardware'}
  ];
  protected readonly viosFieldIds = ['viosSignalName', 'viosSignalClass', 'viosSignalInterface'];
  protected readonly hardwareFieldIds = ['hwControllerPinName', 'hwPinAbbreviation', 'hwPinClass', 'hwPinElectricalInterface'];

  metadata: Record<string, string | boolean> = this.createDefaultMetadata();
  createIcdSuccessVisible = false;
  createIcdSuccessMessage = '';
  createIcdSuccessLinkUrl = '';
  createIcdSuccessLinkLabel = '';
  private readonly emailPreferenceStatusKeys = new Set<string>([
    'allUsersEmailStatus',
    'icdeEngineerEmailStatus',
    'deviceDreEmailStatus',
    'electricalArchitectEmailStatus',
    'deviceVseEmailStatus',
    'electricalVseEmailStatus',
    'deviceEgmEmailStatus',
    'architectEgmEmailStatus'
  ]);

  cols: ColDef[] = [];
  // Single source of truth for cadence merge matching/tie-break behavior.
  // Order matters: earlier entries carry more weight in "closest match" selection.
  // the candidateMatch boolean indicates whether this field can qualify rows as potential matches
  private readonly cadenceMergeMatchFields: CadenceMergeMatchField[] = [
    {path: 'deviceIo.devicePinName', candidateMatch: true},
    {path: 'deviceIo.circuit.circNO', candidateMatch: true},
    {path: 'deviceIo.deviceIOType', candidateMatch: false},
    {path: 'deviceIo.deviceIOValue', candidateMatch: false},
    {path: 'deviceIo.ioTwist', candidateMatch: false},
    {path: 'deviceIo.ioShield', candidateMatch: false},
    {path: 'deviceIo.networkGroup', candidateMatch: false},
    {path: 'deviceIo.pinType', candidateMatch: false}
  ];

  templateCols: ColDef[] = [];
  inventoryCols: ColDef[] = [];
  protected readonly matrixType = signal<'powertrain' | 'aggregator'>('powertrain');

  ngOnInit(): void {
    this.defaultColDef = wireDefaultIoMatrixMergeTooltips(this.defaultColDef);
    const routeType = this.route.snapshot?.data?.['matrixType'] as 'powertrain' | 'aggregator' | undefined;
    this.matrixType.set(routeType ?? 'powertrain');
    if (this.matrixType() === 'aggregator') {
      this.permissionUrl = '/esciUI/aggregatorIoMatrixIcd/permission';
    }
    this.buildInventoryColumns();
    this.rebuildIoMatrixColumns();
    afterNextRender(() => {
      this.permission.set(false);
      this.esciService.getPermissionAndFileSizeLimit(this, false);
      this.loadObjectCodes();
      this.rebuildTemplateColumns();
      this.loadTemplateInventory();
    }, {injector: this.injector});
  }

  protected buildInventoryColumns(): void {
    const selectionColumn: ColDef = {
      colId: 'selectTemplate',
      headerName: '',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      editable: false,
      sortable: false,
      filter: false,
      resizable: false,
      width: 50,
      minWidth: 50,
      maxWidth: 60
    };
    const pinCountColumn: ColDef = {
      field: 'pinCount',
      headerName: 'Pins',
      editable: false,
      valueGetter: params => {
        const assignedPins = Number(params?.data?.pinCount ?? params?.data?.Pin_Count ?? 0);
        const assignableRows = Number(params?.data?.assignableRowCount ?? params?.data?.Assignable_Row_Count ?? assignedPins);
        return `${assignedPins}/${assignableRows}`;
      }
    };
    const auditColumns: ColDef[] = [
      {
        field: 'createdBy',
        headerName: 'Created By',
        editable: false,
        valueGetter: params => params?.data?.createdBy ?? params?.data?.Created_By ?? ''
      },
      {
        field: 'createdDate',
        headerName: 'Created Date',
        editable: false,
        valueGetter: params => params?.data?.createdDate ?? params?.data?.Created_Date ?? '',
        valueFormatter: params => this.formatDateTime(params?.value)
      },
      {
        field: 'updatedBy',
        headerName: 'Last Updated By',
        editable: false,
        valueGetter: params => params?.data?.updatedBy ?? params?.data?.Updated_By ?? ''
      },
      {
        field: 'updatedDate',
        headerName: 'Updated Date',
        editable: false,
        valueGetter: params => params?.data?.updatedDate ?? params?.data?.Updated_Date ?? '',
        valueFormatter: params => this.formatDateTime(params?.value)
      }
    ];

    if (this.matrixType() === 'aggregator') {
      this.inventoryCols = [
        selectionColumn,
        {field: 'id', headerName: 'Template ID', editable: false},
        {field: 'objectCode', headerName: 'Object Code', editable: false},
        {field: 'architecture', headerName: 'Architecture', editable: false},
        {field: 'powerType', headerName: 'Power Type', editable: false},
        {field: 'keyword', headerName: 'Keyword', editable: false},
        {field: 'program', headerName: 'Program', editable: false},
        {field: 'modelYear', headerName: 'Model Year', editable: false},
        pinCountColumn,
        ...auditColumns
      ];
      return;
    }

    this.inventoryCols = [
      selectionColumn,
      {field: 'id', headerName: 'Template ID', editable: false},
      {field: 'objectCode', headerName: 'Object Code', editable: false},
      {field: 'objectCodeInst', headerName: OBJECT_CODE_INST_LABEL, editable: false},
      {field: 'architecture', headerName: 'Architecture', editable: false},
      {field: 'cadenceVer', headerName: 'Cadence', editable: false},
      {field: 'program', headerName: 'Program', editable: false},
      {field: 'modelYear', headerName: 'Model Year', editable: false},
      {
        colId: 'variant',
        headerName: 'Variant',
        editable: false,
        valueGetter: params => {
          const variant = params?.data?.variant ?? '';
          return variant ? variant : 'Original';
        }
      },
      pinCountColumn,
      ...auditColumns
    ];
  }

  ngAfterViewChecked(): void {
    // Export permission can resolve after grid/data setup; keep footer actions in sync once view is stable.
    if (this.canExport && !this.downloadActionsContainer?.isConnected) {
      this.ensureDownloadButtonsInPaginationBar();
    }
    this.updateDownloadButtonsDisabledState();
  }

  exportReport(): void {
    this.updateDownloadButtonsDisabledState();
    if (!this.canExport) {
      this.messageService.processError('User does not have export permission.');
      return;
    }
    const reportRequest = this.getReportDownloadRequest();
    if (reportRequest) {
      this.downloadFromRequest(reportRequest, `${this.getReportFileNamePrefix()}-${CommonHelperService.getTimeStampForReport()}.xlsx`);
      return;
    }
    if (!this.isGridApiAlive(this.gridApi)) {
      this.messageService.processError('Report export is unavailable before the grid is loaded.');
      return;
    }
    if (this.gridApi.getDisplayedRowCount() < 1) {
      this.messageService.processError('There are no rows to export.');
      return;
    }
    this.excelService.exportGridDataAsExcelFile(this.gridApi, this.getReportFileNamePrefix());
  }

  exportHistory(): void {
    this.updateDownloadButtonsDisabledState();
    if (!this.canExport) {
      this.messageService.processError('User does not have export permission.');
      return;
    }
    const historyRequest = this.getHistoryDownloadRequest();
    if (!historyRequest) {
      return;
    }
    this.downloadFromRequest(historyRequest, `${this.getHistoryFileNamePrefix()}-${CommonHelperService.getTimeStampForReport()}.xlsx`);
  }

  protected getReportDownloadRequest(): Observable<ArrayBuffer> | null {
    return null;
  }

  protected getHistoryDownloadRequest(): Observable<ArrayBuffer> | null {
    const matrixId = this.matrixId();
    if (!matrixId) {
      this.messageService.processError('Select Object Code and Architecture first.');
      return null;
    }
    return this.esciService.getIoMatrixHistory(matrixId, this.cadenceVer || undefined);
  }

  protected getReportFileNamePrefix(): string {
    return 'IoMatrixReport';
  }

  protected getHistoryFileNamePrefix(): string {
    return 'IoMatrixHistory';
  }

  protected downloadFromRequest(request$: Observable<ArrayBuffer>, fileName: string): void {
    this.exporting = true;
    this.updateDownloadButtonsDisabledState();
    request$
      .pipe(
        take(1),
        timeout({first: 120000})
      )
      .pipe(finalize(() => {
        this.clearExportingState();
      }))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ArrayBuffer) => {
          // Clear loading state as soon as payload is available; waiting for stream completion can
          // leave the spinner visible even after browser download starts.
          this.clearExportingState();
          if (!data || data.byteLength < 1) {
            this.messageService.processError('Export returned empty content.');
            return;
          }
          CommonHelperService.downloadFile(new Blob([data], {type: 'application/octet-stream;charset=utf-8'}), fileName);
        },
        error: (err: any) => {
          this.messageService.processError(err?.error?.message || err?.message || err || 'Export failed.');
        }
      });
  }

  private clearExportingState(): void {
    if (!this.exporting) {
      return;
    }
    this.exporting = false;
    this.updateDownloadButtonsDisabledState();
  }

  onContextChanged(): void {
    this.ioMatrixCreatedSignal.set(false);
    this.outdatedCadence = false;
    this.setIoMatrixRows([]);
    this.templateRows = [];
    this.cadenceVer = '';
    this.deviceIoCadenceVer = '';
    this.latestSavedCadenceVer = '';
    this.cadenceUpgradeAvailable = false;
    this.cadenceMergePreviewActive = false;
    this.cadenceMergeSourceCadenceVer = '';
    this.deviceIoSyncPreviewActive = false;
    this.deviceIoSyncSummary = '';
    this.deviceIoDriftSummary = null;
    this.lastLoadedSavedRows = [];
    this.lastLoadedLiveRows = [];
    this.inlineMergeOriginalValueDisplay = false;
    this.matrixInUse = false;
    if (!this.objectCode || !this.objectCodeInst) {
      this.refMapWarning = false;
    }
    this.updateDownloadButtonsDisabledState();
  }

  onObjectCodeChanged(): void {
    this.lastCommittedObjectCode = this.objectCode;
    this.lastCommittedObjectCodeInst = '';
    this.objectCodeInst = '';
    this.objectCodeInstQuerySignal.set('');
    this.objectCodeInstLabelSignal.set(OBJECT_CODE_INST_LABEL);
    this.objectCodeInstModeSignal.set('OCI');
    this.objectCodeInstSelectedSignal.set(false);
    this.objectCodeInstOptions = [];
    this.architecture = '';
    if (!this.objectCode) {
      this.objectCodeInstOptions = [];
      this.architectures = [];
      this.architecture = '';
      this.onContextChanged();
      return;
    }
    this.objectCodeQuerySignal.set(this.objectCode);
    this.architectures = [];
    this.onContextChanged();
    this.loadObjectCodeInsts(this.objectCode);
  }

  onObjectCodeCleared(): void {
    this.objectCode = '';
    this.objectCodeQuerySignal.set('');
    this.lastCommittedObjectCode = '';
    this.onObjectCodeChanged();
  }

  onObjectCodeInstChanged(event?: { originalEvent?: Event }): void {
    const isUserSelection = !!event?.originalEvent;
    if (!isUserSelection && !this.objectCodeInstSelectedSignal()) {
      return;
    }
    this.objectCodeInstSelectedSignal.set(true);
    this.lastCommittedObjectCodeInst = this.objectCodeInst;
    this.handleObjectCodeInstContextChange();
  }

  onObjectCodeInstCleared(): void {
    this.objectCodeInst = '';
    this.objectCodeInstQuerySignal.set('');
    this.objectCodeInstSelectedSignal.set(false);
    this.lastCommittedObjectCodeInst = '';
    this.handleObjectCodeInstContextChange();
  }

  onObjectCodeInputBlur(): void {
    this.commitObjectCodeExactMatch();
  }

  onObjectCodeInputEnter(event: KeyboardEvent): void {
    if (this.commitObjectCodeExactMatch()) {
      event.preventDefault();
    }
  }

  onObjectCodeInstInputEnter(event: KeyboardEvent): void {
    if (this.commitObjectCodeInstExactMatch()) {
      event.preventDefault();
    }
  }

  onObjectCodeInstInputBlur(): void {
    this.commitObjectCodeInstExactMatch();
  }

  private commitObjectCodeExactMatch(): boolean {
    const exactMatch = this.findCaseInsensitiveExactMatch(this.objectCode, this.objectCodesSignal());
    if (!exactMatch) {
      return false;
    }
    this.objectCode = exactMatch;
    if (this.lastCommittedObjectCode === exactMatch) {
      return false;
    }
    this.onObjectCodeChanged();
    return true;
  }

  private commitObjectCodeInstExactMatch(): boolean {
    const exactMatch = this.findCaseInsensitiveExactMatch(
      this.objectCodeInst,
      this.objectCodeInstOptionsSignal().map(option => option.value)
    );
    if (exactMatch === undefined) {
      return false;
    }
    this.objectCodeInst = exactMatch;
    if (this.lastCommittedObjectCodeInst === exactMatch && this.objectCodeInstSelectedSignal()) {
      return false;
    }
    this.onObjectCodeInstChanged({originalEvent: new Event('blur')});
    return true;
  }

  protected setObjectCodeInstSelected(selected: boolean): void {
    this.objectCodeInstSelectedSignal.set(!!selected);
  }

  private handleObjectCodeInstContextChange(): void {
    this.architecture = '';
    this.architectures = [];
    this.onContextChanged();
    if (!this.objectCode || !this.objectCodeInstSelectedSignal()) {
      return;
    }
    this.metadata = {
      ...this.metadata,
      objectCodeInst: this.objectCodeInst
    };
    this.refreshRefMapWarning();
    this.loadArchitectures(this.objectCode, this.objectCodeInst);
  }

  onArchitectureChanged(): void {
    if (!this.objectCode || !this.objectCodeInstSelectedSignal()) {
      this.architecture = '';
      return;
    }
    this.onContextChanged();
    this.autoLoadIoMatrixForSelectedContext();
  }

  private loadObjectCodes(): void {
    this.esciService.getIcdObjectCodes().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (codes) => {
        const nextCodes = codes || [];
        this.objectCodes = nextCodes;
        this.objectCodeQuerySignal.set('');
      },
      error: () => {
        this.objectCodes = [];
        this.objectCodeQuerySignal.set('');
        this.messageService.processError('Failed to load Object Code list.');
      }
    });
  }

  private loadObjectCodeInsts(objectCode: string): void {
    const requestId = ++this.objectCodeInstsRequestId;
    this.esciService.getIcdObjectCodeInsts(objectCode).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (insts) => {
        if (requestId !== this.objectCodeInstsRequestId) {
          return;
        }
        const normalizedInsts = (insts || []).map(inst => inst ?? '');
        if (!this.shouldUseControllerDesignatorMode(objectCode, normalizedInsts)) {
          this.applyObjectCodeInstOptions('OCI', normalizedInsts);
          return;
        }
        this.esciService.getIcdControllerDesignators().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (designators) => {
            if (requestId !== this.objectCodeInstsRequestId) {
              return;
            }
            this.applyObjectCodeInstOptions('CONTROLLER_DESIGNATOR', designators || []);
          },
          error: () => {
            if (requestId !== this.objectCodeInstsRequestId) {
              return;
            }
            this.applyObjectCodeInstOptions('CONTROLLER_DESIGNATOR', []);
            this.messageService.processError('Failed to load Controller Designator list.');
          }
        });
      },
      error: () => {
        if (requestId !== this.objectCodeInstsRequestId) {
          return;
        }
        this.objectCodeInstOptions = [];
        this.objectCodeInstLabelSignal.set(OBJECT_CODE_INST_LABEL);
        this.objectCodeInstModeSignal.set('OCI');
        this.messageService.processError('Failed to load Object Code Inst list.');
      }
    });
  }

  private applyObjectCodeInstOptions(mode: 'OCI' | 'CONTROLLER_DESIGNATOR', options: string[]): void {
    this.objectCodeInstModeSignal.set(mode);
    this.objectCodeInstLabelSignal.set(mode === 'CONTROLLER_DESIGNATOR'
      ? 'Controller Designator'
      : OBJECT_CODE_INST_LABEL);
    const normalizedInsts = (options || []).map(inst => inst ?? '');
    this.objectCodeInstOptions = normalizedInsts.map(inst => ({
      label: inst || '(Empty)',
      value: inst
    }));
    if (this.objectCodeInstOptions.length === 1) {
      this.objectCodeInst = this.objectCodeInstOptions[0].value;
      this.objectCodeInstSelectedSignal.set(true);
      this.lastCommittedObjectCodeInst = this.objectCodeInst;
      this.handleObjectCodeInstContextChange();
    }
    if (!this.objectCodeInstOptions.length) {
      this.messageService.processError(mode === 'CONTROLLER_DESIGNATOR'
        ? 'No Controller Designator values are configured. Contact an eSCI Admin.'
        : 'No Object Code Inst options are available for the selected Object Code.');
    }
  }

  private shouldUseControllerDesignatorMode(objectCode: string, objectCodeInsts: string[]): boolean {
    if (!(objectCode || '').toUpperCase().startsWith('A')) {
      return false;
    }
    return !objectCodeInsts.some(inst => !!(inst || '').trim());
  }

  filterObjectCodeSuggestions(event: { query?: string }): void {
    this.objectCodeQuerySignal.set(event?.query ?? '');
  }

  filterObjectCodeInstSuggestions(event: { query?: string }): void {
    this.objectCodeInstQuerySignal.set(event?.query ?? '');
  }

  private findCaseInsensitiveExactMatch(value: string, candidates: string[]): string | undefined {
    const normalizedValue = (value ?? '').trim().toLowerCase();
    if (!normalizedValue) {
      return undefined;
    }
    return (candidates || []).find(candidate => (candidate ?? '').trim().toLowerCase() === normalizedValue);
  }

  private loadArchitectures(objectCode: string, objectCodeInst: string): void {
    this.esciService.getIcdArchitectures(objectCode).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (architectures) => this.filterArchitecturesByDeviceIoRows(objectCode, objectCodeInst, architectures || []),
      error: () => {
        this.architectures = [];
        this.messageService.processError('Failed to load Architecture list.');
      }
    });
  }

  loadIoMatrixRows(): void {
    if (!this.canCreateIoMatrix()) {
      this.messageService.processError('Select Object Code, Object Code Inst, and Architecture first.');
      return;
    }
    this.autoLoadIoMatrixForSelectedContext();
  }

  private autoLoadIoMatrixForSelectedContext(): void {
    if (!this.canCreateIoMatrix()) {
      return;
    }
    this.esciService.getIoMatrixSourceRows(this.objectCode, this.objectCodeInst, this.architecture)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: sourceRows => {
        const nextSourceRows = sourceRows || [];
        const cadenceFromSource = this.latestSourceCadenceFromRows(nextSourceRows);
        this.deviceIoCadenceVer = cadenceFromSource;
        this.cadenceVer = cadenceFromSource;
        if (!nextSourceRows.length) {
          const unavailableArchitecture = this.architecture;
          this.setIoMatrixRows([]);
          this.templateRows = [];
          this.ioMatrixCreatedSignal.set(false);
          this.latestSavedCadenceVer = '';
          this.cadenceUpgradeAvailable = false;
          this.matrixInUse = false;
          this.architecture = '';
          this.architectures = this.architectures.filter(architecture => architecture !== unavailableArchitecture);
          this.messageService.processError('No Device IO rows found for selected Object Code and Architecture.');
          return;
        }
        this.loadRowsWithSavedOverlay(nextSourceRows, cadenceFromSource);
      },
      error: (err) => this.messageService.processError(err?.error?.message || 'Failed to load IO matrix source rows.')
    });
  }

  private filterArchitecturesByDeviceIoRows(objectCode: string, objectCodeInst: string, architectures: string[]): void {
    const candidateArchitectures = (architectures || []).filter(architecture => !!architecture);
    if (!objectCode || !candidateArchitectures.length) {
      this.architectures = [];
      return;
    }

    forkJoin(candidateArchitectures.map(architecture =>
      this.esciService.getIoMatrixSourceRows(objectCode, objectCodeInst, architecture).pipe(
        map(rows => ({architecture, hasRows: (rows || []).length > 0})),
        catchError(() => of({architecture, hasRows: false}))
      ))).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: results => {
        this.architectures = results
          .filter(result => result.hasRows)
          .map(result => result.architecture);
        if (!this.architecture && this.architectures.length === 1) {
          this.architecture = this.architectures[0];
          this.onArchitectureChanged();
          return;
        }
        if (this.architecture && !this.architectures.includes(this.architecture)) {
          this.architecture = '';
        }
      },
      error: () => {
        this.architectures = [];
        this.messageService.processError('Failed to load Architecture list.');
      }
    });
  }

  private loadRowsWithSavedOverlay(sourceRows: IoMatrixRow[], sourceCadence: string): void {
    this.esciService.getIoMatrixSnapshots().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: snapshots => {
        const matrixId = this.matrixId();
        const matrixSnapshots = (snapshots || []).filter(snapshot => snapshot.matrixId === matrixId);
        const sameCadenceSnapshot = matrixSnapshots.find(snapshot => snapshot.cadenceVer === sourceCadence);
        const latestSavedCadence = this.pickLatestCadence(matrixSnapshots.map(snapshot => snapshot.cadenceVer));
        this.latestSavedCadenceVer = latestSavedCadence;
        this.cadenceUpgradeAvailable = !!(matrixSnapshots.length && sourceCadence && latestSavedCadence && sourceCadence !== latestSavedCadence);
        this.matrixInUse = sameCadenceSnapshot?.inUse || false;

        if (!sameCadenceSnapshot) {
          if (latestSavedCadence && sourceCadence && latestSavedCadence !== sourceCadence) {
            // Source moved on; keep showing the latest saved matrix until the user runs an upgrade.
            this.cadenceVer = latestSavedCadence;
            this.esciService.getIoMatrixRows(matrixId, latestSavedCadence).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
              next: response => {
                const nextSavedRows = response?.rows || [];
                this.applyIoMatrixLoadMetadata(response);
                this.setIoMatrixRows(nextSavedRows);
                this.templateRows = structuredClone(nextSavedRows);
                this.ioMatrixCreatedSignal.set(nextSavedRows.length > 0);
                this.loadTemplateInventory();
              },
              error: () => {
                this.setIoMatrixRows(sourceRows);
                this.templateRows = structuredClone(sourceRows);
                this.ioMatrixCreatedSignal.set(sourceRows.length > 0);
                this.loadTemplateInventory();
              }
            });
            return;
          }
          this.setIoMatrixRows(sourceRows);
          this.templateRows = structuredClone(sourceRows);
          this.ioMatrixCreatedSignal.set(sourceRows.length > 0);
          this.loadTemplateInventory();
          return;
        }

        this.esciService.getIoMatrixRows(matrixId, sourceCadence || undefined).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: response => {
            const nextSavedRows = response?.rows || [];
            this.applyIoMatrixLoadMetadata(response);
            this.setIoMatrixRows(nextSavedRows);
            this.templateRows = structuredClone(nextSavedRows);
            this.ioMatrixCreatedSignal.set(nextSavedRows.length > 0);
            this.loadTemplateInventory();
          },
          error: () => {
            this.setIoMatrixRows(sourceRows);
            this.templateRows = structuredClone(sourceRows);
            this.ioMatrixCreatedSignal.set(sourceRows.length > 0);
            this.loadTemplateInventory();
          }
        });
      },
      error: () => {
        this.latestSavedCadenceVer = '';
        this.cadenceUpgradeAvailable = false;
        this.matrixInUse = false;
        this.setIoMatrixRows(sourceRows);
        this.templateRows = structuredClone(sourceRows);
        this.ioMatrixCreatedSignal.set(sourceRows.length > 0);
        this.loadTemplateInventory();
      }
    });
  }

  protected applyIoMatrixLoadMetadata(response: IoMatrixLoadResponse | null | undefined): void {
    this.deviceIoDriftSummary = response?.driftSummary ?? null;
    this.lastLoadedSavedRows = structuredClone(response?.rows || []);
    this.lastLoadedLiveRows = structuredClone(response?.liveDeviceIoRows || []);
    if (!this.deviceIoSyncPreviewActive) {
      this.deviceIoSyncSummary = '';
    }
  }

  startDeviceIoSync(): void {
    if (!this.deviceIoDriftDetected) {
      this.messageService.processError('Device IO is already in sync with the saved matrix.');
      return;
    }
    if (this.dataChanged) {
      this.messageService.processError('Save or reset the current changes before syncing from Device IO.');
      return;
    }
    if (!this.lastLoadedSavedRows.length) {
      this.messageService.processError('Load a saved matrix before syncing from Device IO.');
      return;
    }
    this.syncingDeviceIo = true;
    try {
      const mergedRows = buildDeviceIoDiffRows(
        this.lastLoadedSavedRows,
        this.lastLoadedLiveRows,
        POWERTRAIN_DEVICE_IO_DIFF_FIELDS,
        (row, mode, original) => this.decorateDeviceIoSyncRow(row, mode, original)
      );
      if (!mergedRows.length) {
        this.messageService.processError('Device IO sync produced no rows. Reload the matrix and try again.');
        return;
      }
      this.deviceIoSyncPreviewActive = true;
      this.deviceIoSyncSummary = this.buildDeviceIoSyncSummary(mergedRows);
      this.inlineMergeOriginalValueDisplay = true;
      this.setIoMatrixRows(mergedRows);
      this.templateRows = structuredClone(mergedRows);
      this.dataChanged = true;
      if (this.isGridApiAlive(this.gridApi)) {
        this.gridApi.refreshCells({force: true});
        this.gridApi.redrawRows();
      }
    } finally {
      this.syncingDeviceIo = false;
    }
  }

  protected abandonIoMatrixMergePreviews(): void {
    this.cadenceMergePreviewActive = false;
    this.cadenceMergeSourceCadenceVer = '';
    this.deviceIoSyncPreviewActive = false;
    this.deviceIoSyncSummary = '';
    this.inlineMergeOriginalValueDisplay = false;
    this.dataChanged = false;
  }

  cancelDeviceIoSync(): void {
    this.deviceIoSyncPreviewActive = false;
    this.deviceIoSyncSummary = '';
    this.inlineMergeOriginalValueDisplay = false;
    this.dataChanged = false;
    this.autoLoadIoMatrixForSelectedContext();
  }

  private decorateDeviceIoSyncRow(row: IoMatrixRow, mode: CadenceMergeRowMode,
                                  original?: IoMatrixRow): IoMatrixRow {
    if (mode === 'deleted') {
      (row as any).state = 'deleted';
      return row;
    }
    if (mode === 'new') {
      (row as any).state = 'new';
      row.id = undefined;
      row.connectorAndPin = '';
      row.variant = '';
      row.viosSignalName = '';
      row.viosSignalClass = '';
      row.viosSignalInterface = '';
      row.hwControllerPinName = '';
      row.hwPinAbbreviation = '';
      row.hwPinClass = '';
      row.hwPinElectricalInterface = '';
      row.changes = new Map<string, string>();
      return row;
    }
    if (mode === 'changed') {
      (row as any).state = 'saved';
      row.changes = row.changes ?? new Map<string, string>();
      row.originalRowData = original ?? row.originalRowData;
      return row;
    }
    (row as any).state = 'saved';
    row.changes = new Map<string, string>();
    row.originalRowData = original;
    return row;
  }

  private buildDeviceIoSyncSummary(rows: IoMatrixRow[]): string {
    let changed = 0;
    let added = 0;
    let removed = 0;
    for (const row of rows || []) {
      if ((row as any).state === 'new') {
        added++;
      } else if ((row as any).state === 'deleted') {
        removed++;
      } else if (row.changes && row.changes.size > 0) {
        changed++;
      }
    }
    const parts = [
      changed ? `${changed} changed` : '',
      added ? `${added} new` : '',
      removed ? `${removed} removed` : ''
    ].filter(Boolean);
    return `Device IO sync preview: ${parts.join(', ') || 'no differences'}. Save to accept or Reset Changes to keep the saved snapshot.`;
  }

  protected pickLatestCadence(cadences: string[]): string {
    const normalizedCadences = (cadences || []).filter(cadence => !!cadence);
    if (!normalizedCadences.length) {
      return '';
    }
    const sortedCadences = [...normalizedCadences].sort((a, b) =>
      a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'})
    );
    return sortedCadences[sortedCadences.length - 1] || '';
  }

  /** Device IO rows for one scope can span several cadences; the upgrade target is the newest, not the first row. */
  protected latestSourceCadenceFromRows(sourceRows: IoMatrixRow[]): string {
    return this.pickLatestCadence((sourceRows || []).map(row => row?.cadenceVer || ''));
  }

  private applySavedConnectorValues(sourceRows: IoMatrixRow[], savedRows: IoMatrixRow[]): IoMatrixRow[] {
    if (!savedRows.length) {
      return sourceRows;
    }
    const savedByStableKey = new Map<string, IoMatrixRow>();
    for (const savedRow of savedRows) {
      const stableKey = this.buildSourceRowStableKey(savedRow);
      if (stableKey && !savedByStableKey.has(stableKey)) {
        savedByStableKey.set(stableKey, savedRow);
      }
    }
    return sourceRows.map(sourceRow => {
      const stableKey = this.buildSourceRowStableKey(sourceRow);
      const savedRow = stableKey ? savedByStableKey.get(stableKey) : undefined;
      if (!savedRow) {
        return sourceRow;
      }
      const nextRow = {...sourceRow};
      this.copySavedUserEditedMatrixFields(nextRow, savedRow);
      return nextRow;
    });
  }

  /** Connector/pin, variant, VIOS, and Hardware are matrix-owned — Device IO cadence rows do not carry them. */
  private copySavedUserEditedMatrixFields(target: IoMatrixRow, saved: IoMatrixRow): void {
    target.id = saved.id;
    target.connectorAndPin = saved.connectorAndPin || '';
    target.variant = saved.variant ?? '';
    target.viosSignalName = saved.viosSignalName ?? '';
    target.viosSignalClass = saved.viosSignalClass ?? '';
    target.viosSignalInterface = saved.viosSignalInterface ?? '';
    target.hwControllerPinName = saved.hwControllerPinName ?? '';
    target.hwPinAbbreviation = saved.hwPinAbbreviation ?? '';
    target.hwPinClass = saved.hwPinClass ?? '';
    target.hwPinElectricalInterface = saved.hwPinElectricalInterface ?? '';
  }

  protected buildCadenceMergeRows(latestRows: IoMatrixRow[], savedRows: IoMatrixRow[]): IoMatrixRow[] {
    const latest = latestRows || [];
    const saved = savedRows || [];
    if (!saved.length) {
      return latest.map(row => this.decorateMergeRow(row, undefined, 'new'));
    }

    const savedByIndex = new Map<number, IoMatrixRow>();
    saved.forEach((row, index) => savedByIndex.set(index, row));
    const usedSavedIndexes = new Set<number>();
    const resultRows: IoMatrixRow[] = [];

    latest.forEach(latestRow => {
      const candidateIndexes: number[] = [];
      saved.forEach((savedRow, savedIndex) => {
        if (usedSavedIndexes.has(savedIndex)) {
          return;
        }
        if (this.rowsMatchByConfiguredCandidateFields(latestRow, savedRow)) {
          candidateIndexes.push(savedIndex);
        }
      });

      if (!candidateIndexes.length) {
        resultRows.push(this.decorateMergeRow(latestRow, undefined, 'new'));
        return;
      }

      const sortedByCloseness = candidateIndexes
        .map(savedIndex => ({
          savedIndex,
          score: this.calculateRowClosenessScore(latestRow, savedByIndex.get(savedIndex))
        }))
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }
          return a.savedIndex - b.savedIndex;
        });
      const winner = sortedByCloseness[0];
      const matchedSavedRow = winner ? savedByIndex.get(winner.savedIndex) : undefined;
      if (!matchedSavedRow) {
        resultRows.push(this.decorateMergeRow(latestRow, undefined, 'new'));
        return;
      }

      usedSavedIndexes.add(winner.savedIndex);
      resultRows.push(this.decorateMergeRow(latestRow, matchedSavedRow, 'matched'));
    });

    saved.forEach((savedRow, savedIndex) => {
      if (usedSavedIndexes.has(savedIndex)) {
        return;
      }
      resultRows.push(this.decorateMergeRow(savedRow, savedRow, 'deleted'));
    });

    return resultRows;
  }

  private decorateMergeRow(latestRow: IoMatrixRow, matchedSavedRow?: IoMatrixRow, mode: 'matched' | 'new' | 'deleted' = 'matched'): IoMatrixRow {
    const baseRow = structuredClone(latestRow || {} as IoMatrixRow) as any;
    baseRow.targetIoMatrixRowId = latestRow?.id;
    const original = matchedSavedRow ? structuredClone(matchedSavedRow as any) : undefined;
    if (mode === 'matched' && matchedSavedRow) {
      this.copySavedUserEditedMatrixFields(baseRow, matchedSavedRow);
      baseRow.state = 'saved';
    } else if (mode === 'deleted') {
      baseRow.state = 'deleted';
    } else {
      baseRow.state = 'new';
      baseRow.id = undefined;
      baseRow.connectorAndPin = baseRow.connectorAndPin || '';
    }
    baseRow.changes = new Map<string, string>();
    baseRow.originalRowData = original;
    return baseRow as IoMatrixRow;
  }

  private calculateRowClosenessScore(left: IoMatrixRow, right?: IoMatrixRow): number {
    if (!right) {
      return -1;
    }
    let score = 0;
    const totalFields = this.cadenceMergeMatchFields.length;
    this.cadenceMergeMatchFields.forEach((field, index) => {
      const leftValue = this.getNormalizedMergeFieldValue(left, field.path);
      const rightValue = this.getNormalizedMergeFieldValue(right, field.path);
      if (!leftValue || !rightValue || leftValue !== rightValue) {
        return;
      }
      // Earlier fields are higher priority, so they receive larger score contribution.
      const priorityWeight = totalFields - index;
      score += priorityWeight;
    });
    return score;
  }

  private rowsMatchByConfiguredCandidateFields(left: IoMatrixRow, right: IoMatrixRow): boolean {
    return this.cadenceMergeMatchFields
      .filter(field => field.candidateMatch)
      .some(field => {
        const leftValue = this.getNormalizedMergeFieldValue(left, field.path);
        const rightValue = this.getNormalizedMergeFieldValue(right, field.path);
        return !!leftValue && !!rightValue && leftValue === rightValue;
      });
  }

  private getNormalizedMergeFieldValue(row: IoMatrixRow, fieldPath: string): string {
    return this.normalizeRowValue(CommonHelperService.getDeepValue(row as any, fieldPath));
  }

  private normalizeRowValue(value: unknown): string {
    return String(value ?? '').trim().toUpperCase();
  }

  protected formatDateTime(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = value instanceof Date ? value : new Date(String(value));
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    const year = parsed.getFullYear();
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    const timezone = this.formatTimezoneShort(parsed);
    return `${month}/${day}/${year} ${hours}:${minutes} ${timezone}`;
  }

  private formatTimezoneShort(value: Date): string {
    const timezonePart = new Intl.DateTimeFormat('en-US', {timeZoneName: 'short'})
      .formatToParts(value)
      .find(part => part.type === 'timeZoneName')?.value;
    return timezonePart || 'UTC';
  }

  private buildSourceRowStableKey(row: IoMatrixRow): string {
    const deviceIo: any = row?.deviceIo || {};
    const circuit: any = deviceIo?.circuit || {};
    const normalize = (value: unknown): string => String(value ?? '').trim().toUpperCase();
    return [
      normalize(deviceIo.devicePinName),
      normalize(deviceIo.deviceIOType),
      normalize(deviceIo.deviceIOValue),
      normalize(deviceIo.ioTwist),
      normalize(deviceIo.ioShield),
      normalize(deviceIo.networkGroup),
      normalize(deviceIo.pinType),
      normalize(circuit.circNO),
      normalize(row?.objectCode || deviceIo?.objectCode?.code),
      normalize(row?.objectCodeInst || deviceIo?.objectCodeInst?.inst),
      normalize(row?.architecture || deviceIo?.platform)
    ].join('|');
  }

  protected setIoMatrixRows(rows: IoMatrixRow[]): void {
    // Loading a different row set (switching matrices, cadence upgrade, reset, post-save rehydrate)
    // discards session-only variant additions; persisted variants reappear from the loaded rows'
    // membership. Otherwise an empty variant column from the previous matrix lingers with nothing checked.
    this.sessionVariantTokens = [];
    this.ioMatrixRows = this.prepareIoMatrixRows(rows);
    const cadenceFromRows = this.latestSourceCadenceFromRows(rows);
    if (cadenceFromRows) {
      this.cadenceVer = cadenceFromRows;
    }
    this.rebuildIoMatrixColumns();
    this.updateDataChanged(this.ioMatrixRows as any);
    this.dataChanged = false;
    this.duplicate = false;
    this.rowsInvalid = false;
    // AG Grid's `[rowData]` binding isn't reliable for clearing or replacing rows in
    // every case (notably emptying after a populated state), so push the new rows
    // through the grid API too. Mirrors the framework's `refreshGrid()` pattern.
    if (this.isGridApiAlive(this.gridApi)) {
      const api: any = this.gridApi as any;
      if (typeof api.setGridOption === 'function') {
        api.setGridOption('rowData', this.ioMatrixRows);
      } else if (typeof api.setRowData === 'function') {
        api.setRowData(this.ioMatrixRows);
      }
      this.gridApi.refreshCells({force: true});
      this.gridApi.redrawRows();
    }
    this.scheduleFitGridWidth();
    this.ensureDownloadButtonsInPaginationBar();
    this.updateDownloadButtonsDisabledState();
  }

  override afterPrimaryGridReady(_params: GridReadyEvent): void {
    this.applyIoMatrixViewVisibility();
    this.styleConnectorPinFilterRow();
    this.ensureDownloadButtonsInPaginationBar();
    this.updateDownloadButtonsDisabledState();
  }

  protected onIoMatrixColumnsRebuilt(): void {
    // Hook for IoMatrixComponent to refresh filter-row styling after column changes.
  }

  protected styleConnectorPinFilterRow(): void {
    if (!this.isGridApiAlive(this.gridApi)) {
      return;
    }
    const allColumns = typeof this.gridApi.getAllGridColumns === 'function'
      ? this.gridApi.getAllGridColumns()
      : (typeof this.gridApi.getAllDisplayedColumns === 'function'
        ? this.gridApi.getAllDisplayedColumns()
        : []);
    const hasConnectorPinColumn = allColumns.some(
      col => col.getColDef?.()?.field === 'connectorAndPin'
    );
    if (!hasConnectorPinColumn) {
      return;
    }
    const gridHost = this.agGridHost?.nativeElement;
    if (!gridHost) {
      return;
    }
    this.syncFilterRowColIds(gridHost);
    // Filter tint is CSS-driven via stable col-id selectors. Clear stale inline colors
    // left by older index-based logic so view toggles cannot swap VIOS/Hardware tints.
    gridHost.querySelectorAll('.ag-header-row-column-filter [col-id]').forEach(cell => {
      (cell as HTMLElement).style.removeProperty('background-color');
    });
  }

  private ensureDownloadButtonsInPaginationBar(): void {
    if (!this.canExport) {
      return;
    }
    if (this.downloadActionsContainer?.isConnected) {
      return;
    }
    const gridHost = this.agGridHost?.nativeElement;
    if (!gridHost) {
      return;
    }
    if (!this.downloadActionsContainer) {
      this.downloadActionsContainer = this.createDownloadActionsContainer();
    }
    this.insertNodeIntoAgGridPaginationPanel(this.downloadActionsContainer, gridHost);
  }

  private createDownloadActionsContainer(): HTMLElement {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.justifyContent = 'flex-start';
    container.style.flex = '1';

    this.downloadReportButton = this.createPaginationActionButton(
      'Download Report',
      'Download report',
      () => this.exportReport()
    );
    this.downloadHistoryButton = this.createPaginationActionButton(
      'Download History',
      'Download history',
      () => this.exportHistory()
    );
    this.downloadHistoryButton.style.marginLeft = '5px';

    container.appendChild(this.downloadReportButton);
    container.appendChild(this.downloadHistoryButton);
    return container;
  }

  private createPaginationActionButton(label: string, title: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    btn.title = title;
    btn.className = 'ag-grid-add-row-pagination-btn';
    btn.addEventListener('click', () => onClick());
    return btn;
  }

  private updateDownloadButtonsDisabledState(): void {
    const shouldDisable = !this.canExport || !this.canDownloadCurrentMatrix() || this.saving || this.exporting
      || this.resetting || this.anyMergePreviewActive;
    if (this.downloadReportButton) {
      this.downloadReportButton.disabled = shouldDisable;
    }
    if (this.downloadHistoryButton) {
      this.downloadHistoryButton.disabled = shouldDisable;
    }
  }

  loadCircuitOptions(): void {
    this.esciService.getIcdTemplateCircuitOptions(this.objectCode, this.architecture).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: options => this.circuitOptions = options && options.length ? options : ['X'],
      error: () => this.circuitOptions = ['X']
    });
  }

  saveIoMatrix(): void {
    if (!this.canSaveIoMatrix() || !this.dataChanged || this.saving) {
      return;
    }
    this.syncIoMatrixRowsFromGrid();
    const rowsToSave = (this.ioMatrixRows || []).filter((row: any) => row?.state !== 'deleted');
    if (!this.areRowsValid(rowsToSave as any)) {
      return;
    }
    this.saving = true;
    this.esciService.saveIoMatrix(
      this.objectCode,
      this.objectCodeInst,
      this.architecture,
      this.cadenceVer || '',
      this.matrixId(),
      rowsToSave,
      this.cadenceMergePreviewActive ? {
        saveAsNewCadence: true,
        sourceCadenceVer: this.cadenceMergeSourceCadenceVer || this.latestSavedCadenceVer || ''
      } : undefined
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.rehydrateIoMatrixRowsAfterSave(rowsToSave.length);
      },
      error: (err) => {
        this.saving = false;
        this.messageService.processError(this.extractErrorMessage(err, `Failed to save ${POWERTRAIN_IO_MATRIX_DISPLAY_NAME}.`));
      }
    });
  }

  private rehydrateIoMatrixRowsAfterSave(expectedRowCount: number): void {
    const currentMatrixId = this.matrixId();
    const currentCadenceVer = this.cadenceVer || '';
    if (!currentMatrixId) {
      this.markIoMatrixRowsSaved();
      this.completeIoMatrixSave();
      return;
    }
    this.esciService.getIoMatrixRows(currentMatrixId, currentCadenceVer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        const hydratedRows = response?.rows || [];
        if (!hydratedRows.length && expectedRowCount > 0) {
          // Preserve locally edited rows when backend read-after-write returns no rows.
          this.markIoMatrixRowsSaved();
        } else {
          this.setIoMatrixRows(hydratedRows);
          this.templateRows = structuredClone(this.ioMatrixRows || []);
          this.ioMatrixCreatedSignal.set(hydratedRows.length > 0);
        }
        this.completeIoMatrixSave();
      },
      error: () => {
        this.markIoMatrixRowsSaved();
        this.completeIoMatrixSave();
      }
    });
  }

  private completeIoMatrixSave(): void {
    this.cadenceMergePreviewActive = false;
    this.cadenceMergeSourceCadenceVer = '';
    this.deviceIoSyncPreviewActive = false;
    this.deviceIoSyncSummary = '';
    this.inlineMergeOriginalValueDisplay = false;
    this.saving = false;
    this.onIoMatrixSavedSuccessfully();
    this.messageService.processSuccess(`${POWERTRAIN_IO_MATRIX_DISPLAY_NAME} saved successfully.`);
  }

  protected onIoMatrixSavedSuccessfully(): void {
    // Hook for child components that need post-save behavior.
  }

  resetIoMatrixChanges(): void {
    if (this.cadenceMergePreviewActive) {
      this.cadenceMergePreviewActive = false;
      this.cadenceMergeSourceCadenceVer = '';
      this.inlineMergeOriginalValueDisplay = false;
      this.dataChanged = false;
      this.autoLoadIoMatrixForSelectedContext();
      return;
    }
    if (this.deviceIoSyncPreviewActive) {
      this.cancelDeviceIoSync();
      return;
    }
    // Bracket the reset path with `resetting = true/false` so the spinner template bindings
    // (`@if (saving || exporting || resetting)` and `[loading]="..."`) actually activate while
    // the work runs. Wrap in try/finally to ensure the flag clears even if grid refresh throws.
    this.resetting = true;
    try {
      const restoredRows = (this.ioMatrixRows || []).map((row: any) => {
        if (row?.originalRowData) {
          return structuredClone(row.originalRowData);
        }
        return structuredClone(row);
      });
      this.setIoMatrixRows(restoredRows);
      if (this.isGridApiAlive(this.gridApi)) {
        this.gridApi.refreshCells({force: true});
        this.gridApi.redrawRows();
      }
    } finally {
      this.resetting = false;
    }
  }

  runCadenceUpgrade(): void {
    const fromCadenceVer = this.latestSavedCadenceVer || this.cadenceVer || '';
    const targetCadenceVer = this.deviceIoCadenceVer || this.cadenceVer || '';
    if (!fromCadenceVer || !targetCadenceVer || fromCadenceVer === targetCadenceVer) {
      this.messageService.processError('No newer Device IO cadence is available for merge.');
      return;
    }
    if (this.latestSavedCadenceVer && fromCadenceVer !== this.latestSavedCadenceVer) {
      this.messageService.processError(`Merge must start from latest saved cadence ${this.latestSavedCadenceVer}.`);
      return;
    }
    this.esciService.triggerIoMatrixCadenceUpgrade(
      this.objectCode,
      this.objectCodeInst,
      this.architecture,
      targetCadenceVer
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: rows => {
        const nextRows = rows || [];
        if (!nextRows.length) {
          this.messageService.processError(
            `No Device IO rows were returned for cadence ${targetCadenceVer}.`);
          return;
        }
        const matrixId = this.matrixId();
        if (!matrixId || !fromCadenceVer) {
          this.applyCadenceUpgradeRows(nextRows, targetCadenceVer, nextRows, fromCadenceVer);
          return;
        }
        this.esciService.getIoMatrixRows(matrixId, fromCadenceVer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: response => {
            const mergedRows = this.buildCadenceMergeRows(nextRows, response?.rows || []);
            this.applyCadenceUpgradeRows(mergedRows, targetCadenceVer, nextRows, fromCadenceVer);
          },
          error: () => this.applyCadenceUpgradeRows(this.buildCadenceMergeRows(nextRows, []), targetCadenceVer, nextRows, fromCadenceVer)
        });
      },
      error: (err) => this.messageService.processError(err?.error?.message || 'Cadence upgrade failed.')
    });
  }

  protected applyCadenceUpgradeRows(rows: IoMatrixRow[], targetCadenceVer: string,
                                    _baselineRows: IoMatrixRow[], sourceCadenceVer: string): void {
    const nextRows = rows || [];

    this.setIoMatrixRows(nextRows);
    this.templateRows = structuredClone(nextRows);
    this.ioMatrixCreatedSignal.set(nextRows.length > 0);
    this.cadenceVer = targetCadenceVer;
    this.deviceIoCadenceVer = targetCadenceVer;
    this.latestSavedCadenceVer = targetCadenceVer;
    this.cadenceUpgradeAvailable = false;
    this.cadenceMergePreviewActive = true;
    this.cadenceMergeSourceCadenceVer = sourceCadenceVer || '';
    this.inlineMergeOriginalValueDisplay = true;
    this.outdatedCadence = false;
    this.dataChanged = true;
    this.messageService.processSuccess('Cadence merge rows loaded for review.');
  }

  addProgramModelYearColumn(): void {
    if (!this.selectedProgram || !this.selectedModelYear) {
      this.messageService.processError('Select Program and Model Year first.');
      return;
    }
    const variant = this.getSelectedTemplateVariant();
    const field = this.toTemplateField(
      this.selectedProgram,
      this.selectedModelYear,
      this.metadata['rpo'] as string,
      this.metadata['additionalRpos'] as string,
      this.metadata['buildPhase'] as string,
      variant
    );
    if (this.templateColumns.some(c => c.field === field)) {
      this.messageService.processError('Program/Model Year already exists in this grid.');
      return;
    }
    this.templateColumns.push({
      field,
      program: this.selectedProgram,
      modelYear: this.selectedModelYear,
      rpo: (this.metadata['rpo'] as string) || '',
      additionalRpos: (this.metadata['additionalRpos'] as string) || '',
      buildPhase: (this.metadata['buildPhase'] as string) || '',
      variant
    });
    this.templateRows.forEach(row => row[field] = row[field] ?? '');
    this.rebuildTemplateColumns();
  }

  protected rebuildTemplateColumns(): void {
    this.templateCols = [
      ...this.buildBaseIoDisplayColumns(),
      this.buildConnectorAndPinColumn(false),
      ...this.buildViosColumns(false),
      ...this.buildHardwareColumns(false),
      ...this.templateColumns.map(c => buildTemplateColDef(c, this.getTemplatePinEditorOptions()))
    ];
    this.applyMergeCellPresentation(this.templateCols);
    this.scheduleFitGridWidth();
    this.applyIoMatrixViewVisibility();
  }

  private buildBaseIoDisplayColumns(): ColDef[] {
    return this.buildIoMatrixReadOnlyDisplayColumns();
  }

  private buildIoMatrixReadOnlyDisplayColumns(): ColDef[] {
    // wrapText is disabled on identifier columns so long device/pin/circuit values
    // do not wrap to a second line (which would be clipped because these columns
    // do not set autoHeight) and so `autoSizeAllColumns` can measure the natural
    // single-line content width. Circuit Description intentionally keeps the
    // inherited wrapText: true and pairs it with autoHeight to render multi-line
    // descriptive text correctly. Device Name is shown in the banner above the grid.
    return [
      {field: 'deviceIo.devicePinName', headerName: 'Device Pin Name', editable: false, width: 170, minWidth: 140, wrapText: false},
      {field: 'deviceIo.deviceIOType', headerName: 'Device IO Type', editable: false, width: 120, minWidth: 110, wrapText: false},
      {field: 'deviceIo.deviceIOValue', headerName: 'Device IO Value', editable: false, width: 140, minWidth: 120, wrapText: false},
      {field: 'deviceIo.circuit.circNO', headerName: 'Circuit Number', editable: false, width: 120, minWidth: 110, wrapText: false},
      {
        field: 'deviceIo.circuit.circScope.circDesc',
        headerName: 'Circuit Description',
        editable: false,
        width: 420,
        minWidth: 320,
        autoHeight: true,
      }
    ];
  }

  private buildConnectorAndPinColumn(editable: boolean): ColDef {
    return this.buildEditableIoMatrixColumn({
      field: 'connectorAndPin',
      headerName: 'Connector and Pin',
      editable: editable ? (params: any) => params?.data?.state !== 'deleted' : false,
      width: 140,
      minWidth: 120
    }, editable, 'connector');
  }

  protected buildEditableIoMatrixColumn(
    col: ColDef,
    editable: boolean,
    columnStyle: 'connector' | 'vios' | 'hardware' = 'connector'
  ): ColDef {
    const styleClasses = IoMatrixWorkflowComponent.getIoMatrixEditableColumnStyleClasses(columnStyle);
    const baseEditable = col.editable;
    const resolvedEditable = editable
      ? (params: any) => {
        if (params?.data?.state === 'deleted') {
          return false;
        }
        return typeof baseEditable === 'function' ? baseEditable(params) : baseEditable !== false;
      }
      : false;
    return {
      ...col,
      editable: resolvedEditable,
      cellClass: styleClasses.cellClass,
      headerClass: styleClasses.headerClass,
      wrapText: col.wrapText ?? false
    };
  }

  private static getIoMatrixEditableColumnStyleClasses(
    columnStyle: 'connector' | 'vios' | 'hardware'
  ): { cellClass: string; headerClass: string } {
    switch (columnStyle) {
      case 'vios':
        return {cellClass: 'io-matrix-vios-column', headerClass: 'io-matrix-vios-column-header'};
      case 'hardware':
        return {cellClass: 'io-matrix-hardware-column', headerClass: 'io-matrix-hardware-column-header'};
      default:
        return {cellClass: 'program-column', headerClass: 'program-column-header'};
    }
  }

  protected applyMergeCellPresentation(columns: ColDef[]): void {
    for (const col of columns || []) {
      const colId = col.colId || '';
      if (col.cellDataType === 'boolean' || colId.startsWith('variant_')) {
        continue;
      }
      const field = col.field || '';
      col.tooltipValueGetter = (params: any) => getIoMatrixMergeCellTooltip(params, field);
      col.cellRenderer = (params: any) => this.renderMergeCellContent(params, field);
    }
  }

  // Always hand ag-grid an element: a string returned from cellRenderer is assigned as innerHTML, so
  // returning a Device IO value directly would let a crafted name or description inject grid nodes.
  private renderMergeCellContent(params: any, field: string): HTMLElement {
    const value = params?.valueFormatted ?? params?.value ?? '';
    const displayValue = String(value ?? '');
    if (!this.inlineMergeOriginalValueDisplay || !field) {
      return buildMergeCellElement(displayValue, '');
    }
    const rendered = renderCadenceMergeCellContent(displayValue, params?.data?.changes?.get(field));
    return typeof rendered === 'string' ? buildMergeCellElement(rendered, '') : rendered;
  }

  protected getTemplatePinEditorOptions(): string[] {
    // Copied per call so a consumer mutating the array cannot leak into other columns.
    return [...TEMPLATE_PIN_EDITOR_OPTIONS];
  }

  protected isTemplateField(field: string): boolean {
    return isTemplateField(field);
  }

  protected normalizeTemplateCircuitSelection(value: unknown): string {
    return normalizeTemplateCircuitSelection(value);
  }

  saveTemplateValues(): void {
    for (const templateColumn of this.templateColumns) {
      const rows = this.templateRows.map(row => ({
        fkIoMatrixRowId: row.id as number,
        icdCircuitSelection: this.normalizeTemplateCircuitSelection(row[templateColumn.field])
      }));
      this.esciService.saveIcdTemplateValues(
        this.objectCode,
        this.objectCodeInst,
        this.architecture,
        this.cadenceVer || '',
        templateColumn.program,
        templateColumn.modelYear,
        templateColumn.rpo,
        templateColumn.additionalRpos,
        templateColumn.buildPhase,
        this.getSelectedTemplateVariant(),
        rows
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => this.messageService.processSuccessWithoutClear(`Saved ${templateColumn.program} ${templateColumn.modelYear}.`),
        error: (err) => this.messageService.processError(err?.error?.message || 'Failed to save template values.')
      });
    }
    this.loadTemplateInventory();
  }

  startFresh(): void {
    if (!this.selectedProgram || !this.selectedModelYear) {
      this.messageService.processError('Select Program and Model Year to clear.');
      return;
    }
    this.esciService.startFreshIcdTemplate(
      this.objectCode,
      this.objectCodeInst,
      this.architecture,
      this.cadenceVer || '',
      this.selectedProgram,
      this.selectedModelYear,
      (this.metadata['rpo'] as string) || '',
      (this.metadata['additionalRpos'] as string) || '',
      (this.metadata['buildPhase'] as string) || '',
      this.getSelectedTemplateVariant()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        const field = this.toTemplateField(
          this.selectedProgram,
          this.selectedModelYear,
          (this.metadata['rpo'] as string) || '',
          (this.metadata['additionalRpos'] as string) || '',
          (this.metadata['buildPhase'] as string) || '',
          this.getSelectedTemplateVariant()
        );
        this.templateRows.forEach(row => row[field] = '');
        this.messageService.processSuccess('Template values cleared. Column retained.');
      },
      error: (err) => this.messageService.processError(err?.error?.message || 'Failed to clear template values.')
    });
  }

  loadTemplateInventory(): void {
    // Templates are a powertrain-only concept: every aggregator matrix row belongs in every ICD built
    // from it, so there is nothing to select and no inventory to show.
    if (this.matrixType() === 'aggregator') {
      this.templateInventory = [];
      this.templateInventoryLoadingSignal.set(false);
      return;
    }
    this.templateInventoryLoadingSignal.set(true);
    const inventoryRequest: Observable<IcdTemplateInventory[]> = this.esciService.getIcdTemplateInventory();
    inventoryRequest.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: rows => {
        this.templateInventoryLoadingSignal.set(false);
        // 200 OK with [] is a legitimate first-load state for new workflows, so we do NOT
        // toast here. The empty grid itself is the user-visible signal. The error path below
        // distinguishes "request failed" with an explicit toast.
        this.templateInventory = rows || [];
        const availableTemplateIds = new Set(
          this.templateInventory
            .map(template => this.getTemplateId(template))
            .filter((id): id is number => Number.isFinite(id))
        );
        this.selectedTemplateIds = this.selectedTemplateIds.filter(id => availableTemplateIds.has(id));
        if (!this.selectedTemplateIds.length) {
          this.icdCreationStarted = false;
        }
        this.syncTemplateDerivedMetadata();
      },
      error: (err) => {
        // Explicit "request failed" toast separate from the empty-data path above.
        this.templateInventoryLoadingSignal.set(false);
        this.messageService.processError(
          err?.error?.message
            || 'Could not load ICD template inventory (request failed). Try again or contact support.');
        this.templateInventory = [];
        this.selectedTemplateIds = [];
        this.icdCreationStarted = false;
        this.syncTemplateDerivedMetadata();
      }
    });
  }

  toggleTemplateSelection(templateId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedTemplateIds.includes(templateId)) {
        this.selectedTemplateIds = [...this.selectedTemplateIds, templateId];
      }
    } else {
      this.selectedTemplateIds = this.selectedTemplateIds.filter(id => id !== templateId);
    }
    this.syncTemplateDerivedMetadata();
  }

  onTemplateInventorySelectionChanged(): void {
    if (!this.gridApi || this.gridApi.isDestroyed()) {
      return;
    }
    const selectedRows = this.gridApi.getSelectedRows() as IcdTemplateInventory[];
    this.selectedTemplateIds = selectedRows
      .map(row => this.getTemplateId(row))
      .filter((id): id is number => Number.isFinite(id));
    if (!this.selectedTemplateIds.length) {
      this.icdCreationStarted = false;
      this.createIcdSubmissionInProgress = false;
    }
    this.syncTemplateDerivedMetadata();
  }

  startIcdCreation(): void {
    if (!this.selectedTemplateIds.length) {
      this.messageService.processError('Select at least one template.');
      return;
    }
    this.syncTemplateDerivedMetadata();
    this.dismissCreateIcdSuccess();
    this.createIcdSubmissionInProgress = false;
    this.icdCreationStarted = true;
  }

  private syncTemplateDerivedMetadata(): void {
    const selectedTemplates = this.templateInventory
      .filter(template => {
        const templateId = this.getTemplateId(template);
        return Number.isFinite(templateId) && this.selectedTemplateIds.includes(templateId as number);
      });
    const architecture = this.extractCommonTemplateValue(selectedTemplates, template => template.architecture);

    this.metadata = {
      ...this.metadata,
      program: this.extractCommonTemplateValue(selectedTemplates, template => template.program),
      modelYear: this.extractCommonTemplateValue(selectedTemplates, template => template.modelYear),
      rpo: this.extractCommonTemplateValue(selectedTemplates, template => template.rpo),
      additionalRpos: this.extractCommonTemplateValue(selectedTemplates, template => template.additionalRpos || ''),
      buildPhase: this.extractCommonTemplateValue(selectedTemplates, template => template.buildPhase),
      objectCode: this.extractCommonTemplateValue(selectedTemplates, template => template.objectCode),
      objectCodeInst: this.extractCommonTemplateValue(selectedTemplates, template => template.objectCodeInst),
      architecture,
      platform: architecture,
      cadence: this.extractCommonTemplateValue(selectedTemplates, template => template.cadenceVer)
    };
  }

  private extractCommonTemplateValue(
    selectedTemplates: IcdTemplateInventory[],
    selector: (template: IcdTemplateInventory) => string
  ): string {
    if (!selectedTemplates.length) {
      return '';
    }
    const values = selectedTemplates
      .map(template => (selector(template) || '').trim())
      .filter(value => !!value);
    if (!values.length) {
      return '';
    }
    const uniqueValues = Array.from(new Set(values));
    return uniqueValues.length === 1 ? uniqueValues[0] : 'Multiple';
  }

  private getTemplateId(template: IcdTemplateInventory | null | undefined): number | undefined {
    if (!template) {
      return undefined;
    }
    return template.id ?? template.templateId;
  }

  createIcds(): void {
    // Aggregators have no templates to select; their equivalent gate is the matrix scope, checked by
    // the subclass before it lets the metadata form open.
    if (this.matrixType() !== 'aggregator' && !this.selectedTemplateIds.length) {
      this.handleCreateIcdError('Select at least one template.');
      return;
    }
    this.dismissCreateIcdSuccess();
    this.createIcdSubmissionInProgress = true;
    this.getCreateIcdsFromTemplatesRequest(
      this.selectedTemplateIds,
      this.serializeMetadataForRequest()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: CreateIcdsFromTemplatesResponse) => {
        const createdIcdIds = response?.createdIcdIds || [];
        const count = createdIcdIds.length;
        if (count < 1) {
          this.createIcdSubmissionInProgress = false;
          this.dismissCreateIcdSuccess();
          this.handleCreateIcdError('ICD create call returned no created IDs.');
          return;
        }
        this.setCreateIcdSuccessState(response, createdIcdIds);
        this.resetCreateIcdMetadata();
        this.icdCreationStarted = false;
        this.createIcdSubmissionInProgress = false;
      },
      error: (err) => {
        this.createIcdSubmissionInProgress = false;
        this.handleCreateIcdError(err?.error?.message || 'Failed to create ICDs.');
      }
    });
  }

  protected getCreateIcdsFromTemplatesRequest(
    templateIds: number[],
    metadata: Record<string, string | boolean>
  ): Observable<CreateIcdsFromTemplatesResponse> {
    if (this.matrixType() === 'aggregator') {
      // No templateIds: the aggregator request carries the matrix scope and the ICD identity instead.
      return this.esciService.createAggregatorIcd(metadata);
    }
    return this.esciService.createIcdsFromTemplates(templateIds, metadata);
  }

  protected handleCreateIcdError(message: string): void {
    this.messageService.processError(message || 'Failed to create ICDs.');
  }

  dismissCreateIcdSuccess(): void {
    this.createIcdSuccessVisible = false;
    this.createIcdSuccessMessage = '';
    this.createIcdSuccessLinkUrl = '';
    this.createIcdSuccessLinkLabel = '';
  }

  private setCreateIcdSuccessState(response: CreateIcdsFromTemplatesResponse, createdIcdIds: number[]): void {
    const count = createdIcdIds.length;
    if (count === 1) {
      const destination = this.resolveSingleIcdDestination(response, createdIcdIds[0]);
      this.createIcdSuccessMessage = 'Created 1 ICD successfully.';
      this.createIcdSuccessLinkUrl = destination;
      this.createIcdSuccessLinkLabel = `Open ICD ${createdIcdIds[0]} in a new tab`;
      this.createIcdSuccessVisible = true;
      return;
    }
    if (count > 1) {
      this.createIcdSuccessMessage = `Created ${count} ICDs successfully.`;
      this.createIcdSuccessLinkUrl = this.resolveIcdSearchDestination();
      this.createIcdSuccessLinkLabel = 'Open ICD Search Results in a new tab';
      this.createIcdSuccessVisible = true;
      return;
    }
    this.dismissCreateIcdSuccess();
  }

  private resolveSingleIcdDestination(response: CreateIcdsFromTemplatesResponse, createdIcdId?: number): string {
    const fallbackPath = Number.isFinite(createdIcdId) ? `/ui/icd/${createdIcdId}` : '';
    const destinationPath = String(response?.singleIcdDestination || fallbackPath).trim();
    if (!destinationPath) {
      return '';
    }
    if (/^https?:\/\//i.test(destinationPath)) {
      return destinationPath;
    }
    return this.resolveAbsoluteUiPath(destinationPath);
  }

  private resolveIcdSearchDestination(): string {
    // `/ui/icd` requires session-backed `icdSearchInfo` and can fail on direct navigation.
    // `/ui/icds?pageNo=1` is the supported entry route that creates a search request and
    // lands directly on the results grid without requiring prior session state.
    return this.resolveAbsoluteUiPath('/ui/icds?pageNo=1');
  }

  private resolveAbsoluteUiPath(pathOrUrl: string): string {
    const rawValue = String(pathOrUrl || '').trim();
    if (!rawValue) {
      return '';
    }
    if (/^https?:\/\//i.test(rawValue)) {
      return rawValue;
    }
    const baseUrl = String(environment.apiUrl || window.location.origin).replace(/\/+$/, '');
    const path = rawValue.startsWith('/') ? rawValue : `/${rawValue}`;
    return `${baseUrl}${path}`;
  }

  onMetadataUserSelected(field: string, selectedUser: SearchUser | null | undefined): void {
    if (!field) {
      return;
    }
    this.metadata[field] = selectedUser?.gmin || '';
  }

  getMetadataUserInitialValue(field: string): string {
    if (!field) {
      return '';
    }
    const value = this.metadata[field];
    return typeof value === 'string' ? value : '';
  }

  setEmailPreferenceStatus(field: string, status: 'ON' | 'OFF'): void {
    if (!field) {
      return;
    }
    this.metadata[field] = status === 'OFF' ? 'OFF' : 'ON';
  }

  isEmailPreferenceOn(field: string): boolean {
    const value = this.metadata[field];
    return String(value || 'ON').toUpperCase() !== 'OFF';
  }

  private serializeMetadataForRequest(): Record<string, string | boolean> {
    return Object.entries(this.metadata || {}).reduce((acc, [key, value]) => {
      if (key === 'option') {
        return acc;
      }
      if (typeof value === 'boolean') {
        acc[key] = value;
      } else {
        const normalizedValue = value ?? '';
        acc[key] = this.emailPreferenceStatusKeys.has(key)
          ? (String(normalizedValue).toUpperCase() === 'OFF' ? 'OFF' : 'ON')
          : normalizedValue;
      }
      return acc;
    }, {} as Record<string, string | boolean>);
  }

  protected refreshRefMapWarning(): void {
    if (!this.objectCode || !this.objectCodeInst) {
      this.refMapWarning = false;
      return;
    }
    this.esciService.getIcdRefMapStatus(this.objectCode, this.objectCodeInst).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (status: any) => {
        // The backend can return an empty {} when no mapping row exists (BaseEntity instance
        // with no id), in which case a plain `!status` truthiness check is `false` and the
        // warning would be suppressed even though there is no usable mapping. Compute the
        // warning from a meaningful identifier on the payload (BaseEntity id) so empty /
        // placeholder responses correctly raise the warning.
        const hasUsableMapping = !!(status && (status.id != null && Number(status.id) > 0));
        this.refMapWarning = !hasUsableMapping;
      },
      error: (err) => {
        // Distinguish "request failed" from "missing data" so users can act on it. We still
        // suppress the warning banner (we don't know if a mapping exists), but a toast tells
        // them why the warning is absent.
        this.refMapWarning = false;
        this.messageService.processError(
          err?.error?.message
            || 'Could not load Reference Mapping status (request failed). Try again or contact support.');
      }
    });
  }

  toTemplateField(program: string, modelYear: string, rpo: string, additionalRpos: string, buildPhase: string,
                  variant?: string): string {
    // Variant identity is IO-Matrix-specific (the raw token is hashed so visually similar variants
    // cannot collide), so it is resolved here and handed to the shared field builder already sanitized.
    return buildTemplateField(program, modelYear, rpo, additionalRpos, buildPhase,
                              this.sanitizeVariantFieldSegment(variant ?? ''));
  }

  protected getSelectedTemplateVariant(): string {
    return '';
  }

  protected rebuildIoMatrixColumns(): void {
    this.cols = [
      ...this.buildBaseIoDisplayColumns(),
      this.buildConnectorAndPinColumn(true),
      ...this.buildViosColumns(),
      ...this.buildHardwareColumns(),
      ...this.buildVariantColumns()
    ];
    this.applyMergeCellPresentation(this.cols);
    this.scheduleFitGridWidth();
    this.applyIoMatrixViewVisibility();
    this.onIoMatrixColumnsRebuilt();
  }

  protected buildViosColumns(editable = true): ColDef[] {
    return [
      this.buildEditableIoMatrixColumn({
        field: 'viosSignalName',
        headerName: 'VIOS Signal Name',
        width: 180,
        minWidth: 140
      }, editable, 'vios'),
      this.buildEditableIoMatrixColumn({
        field: 'viosSignalClass',
        headerName: 'VIOS Signal Class',
        width: 150,
        minWidth: 120
      }, editable, 'vios'),
      this.buildEditableIoMatrixColumn({
        field: 'viosSignalInterface',
        headerName: 'VIOS Signal Interface',
        width: 170,
        minWidth: 130
      }, editable, 'vios')
    ];
  }

  protected buildHardwareColumns(editable = true): ColDef[] {
    return [
      this.buildEditableIoMatrixColumn({
        field: 'hwControllerPinName',
        headerName: 'HW Controller Pin Name',
        width: 180,
        minWidth: 140
      }, editable, 'hardware'),
      this.buildEditableIoMatrixColumn({
        field: 'hwPinAbbreviation',
        headerName: 'HW Pin Abbreviation',
        width: 150,
        minWidth: 120
      }, editable, 'hardware'),
      this.buildEditableIoMatrixColumn({
        field: 'hwPinClass',
        headerName: 'HW Pin Class',
        width: 130,
        minWidth: 110
      }, editable, 'hardware'),
      this.buildEditableIoMatrixColumn({
        field: 'hwPinElectricalInterface',
        headerName: 'HW Pin Electrical Interface',
        width: 190,
        minWidth: 150
      }, editable, 'hardware')
    ];
  }

  protected buildVariantColumns(): ColDef[] {
    return this.getActiveVariantTokens().map(token => this.buildVariantCheckboxColumn(token));
  }

  protected getActiveVariantTokens(): string[] {
    const rowTokens = this.collectDistinctVariantTokens(this.ioMatrixRows);
    return [...new Set([...rowTokens, ...this.sessionVariantTokens])].sort((a, b) => a.localeCompare(b));
  }

  protected collectDistinctVariantTokens(rows: IoMatrixRow[] | null | undefined): string[] {
    const tokens = new Set<string>();
    for (const row of rows || []) {
      for (const token of this.parseVariantTokens(row?.variant)) {
        tokens.add(token);
      }
    }
    return [...tokens].sort((a, b) => a.localeCompare(b));
  }

  protected parseVariantTokens(raw: string | null | undefined): string[] {
    return this.normalizeVariantMembership(raw).split('|').filter(token => !!token);
  }

  protected normalizeVariantMembership(raw: string | null | undefined): string {
    if (!raw) {
      return '';
    }
    const tokens = raw.split('|')
      .map(token => token.trim())
      .filter(token => token.length > 0);
    return [...new Set(tokens)].sort((a, b) => a.localeCompare(b)).join('|');
  }

  protected normalizeTemplateVariant(raw: string | null | undefined): string {
    return (raw ?? '').trim();
  }

  protected buildVariantToken(usage: string, partNumber: string): string {
    return `${(usage || '').trim()}-${(partNumber || '').trim()}`;
  }

  protected rowHasVariantToken(rowVariant: string | null | undefined, token: string): boolean {
    const normalizedToken = (token || '').trim();
    if (!normalizedToken) {
      return true;
    }
    return this.parseVariantTokens(rowVariant).includes(normalizedToken);
  }

  protected addVariantToken(rowVariant: string | null | undefined, token: string): string {
    const normalizedToken = (token || '').trim();
    if (!normalizedToken) {
      return this.normalizeVariantMembership(rowVariant);
    }
    const tokens = this.parseVariantTokens(rowVariant);
    if (!tokens.includes(normalizedToken)) {
      tokens.push(normalizedToken);
    }
    tokens.sort((a, b) => a.localeCompare(b));
    return tokens.join('|');
  }

  protected removeVariantToken(rowVariant: string | null | undefined, token: string): string {
    const normalizedToken = (token || '').trim();
    if (!normalizedToken) {
      return this.normalizeVariantMembership(rowVariant);
    }
    return this.parseVariantTokens(rowVariant)
      .filter(existing => existing !== normalizedToken)
      .join('|');
  }

  protected applyVariantFilter(rows: IoMatrixRow[] | null | undefined, selectedVariant: string): IoMatrixRow[] {
    const normalizedVariant = this.normalizeTemplateVariant(selectedVariant);
    if (!normalizedVariant) {
      return [...(rows || [])];
    }
    return (rows || []).filter(row => this.rowHasVariantToken(row?.variant, normalizedVariant));
  }

  protected applyIoMatrixViewVisibility(): void {
    if (!this.isGridApiAlive(this.gridApi) || typeof this.gridApi.setColumnsVisible !== 'function') {
      return;
    }
    const view = this.selectedViewSignal();
    const showVios = view === 'architecture' || view === 'vios';
    const showHardware = view === 'architecture' || view === 'hardware';
    this.gridApi.setColumnsVisible(this.viosFieldIds, showVios);
    this.gridApi.setColumnsVisible(this.hardwareFieldIds, showHardware);
    this.styleConnectorPinFilterRow();
  }

  protected updateVariantFieldChangeTracking(row: any): void {
    if (!row || row.state === 'deleted') {
      return;
    }
    const preserveNewState = row.state === 'new';
    row.changes = row.changes || new Map<string, string>();
    const originalValue = row.originalRowData?.variant ?? '';
    const currentValue = row.variant ?? '';
    if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
      row.changes.set('variant', `Original Value: ${originalValue}`);
    } else {
      row.changes.delete('variant');
    }
    if (preserveNewState) {
      row.state = 'new';
    } else {
      row.state = row.changes.size > 0 ? 'changed' : 'saved';
    }
    this.syncIoMatrixDataChangedFromRows();
  }

  protected onIoMatrixCellValueChanged(event: CellValueChangedEvent<any>): void {
    const colId = event.column?.getColId?.() || '';
    if (!colId.startsWith('variant_')) {
      return;
    }
    // Variant membership is applied in buildVariantCheckboxColumn().valueSetter only.
    // Handling it here as well double-toggles when AG Grid emits conflicting old/new values.
    if (this.isGridApiAlive(this.gridApi) && event.node && event.column) {
      refreshCellAndDeferredRowRedraw(this.gridApi, event.node, event.column);
    }
    this.syncIoMatrixDataChangedFromRows();
  }

  protected applyVariantCheckboxToggle(row: any, token: string, checked: boolean): void {
    if (!row || !token || row.state === 'deleted') {
      return;
    }
    row.variant = this.normalizeVariantMembership(
      checked ? this.addVariantToken(row.variant, token) : this.removeVariantToken(row.variant, token)
    );
    this.updateVariantFieldChangeTracking(row);
  }

  private syncIoMatrixDataChangedFromRows(): void {
    this.dataChanged = (this.ioMatrixRows || []).some(r => (r as any)?.state !== 'saved');
  }

  protected syncIoMatrixRowsFromGrid(): void {
    if (!this.isGridApiAlive(this.gridApi)) {
      return;
    }
    const syncedRows: IoMatrixRow[] = [];
    this.gridApi.forEachNode(node => {
      if (node.data) {
        syncedRows.push(node.data as IoMatrixRow);
      }
    });
    if (syncedRows.length > 0) {
      this.ioMatrixRows = syncedRows;
    }
  }

  override cellEditingStopped(event: CellEditingStoppedEvent<any>, rows: ValidationModel[]): void {
    super.cellEditingStopped(event, rows);
    if (event.data) {
      this.updateVariantFieldChangeTracking(event.data);
      // Recompute from caller-supplied rows (ioMatrixRows or templateRows), not ioMatrixRows-only sync.
      this.dataChanged = (rows?.some(r => (r as any)?.state !== 'saved') ?? false);
    }
  }

  protected sanitizeVariantFieldSegment(variant: string): string {
    const normalized = this.normalizeTemplateVariant(variant);
    if (!normalized) {
      return 'ORIGINAL';
    }
    const base = normalized.replace(/[^A-Za-z0-9]/g, '_').toUpperCase();
    return `${base}_${IoMatrixWorkflowComponent.variantIdentifierSuffix(normalized)}`;
  }

  protected sanitizeVariantColId(token: string): string {
    const raw = token || '';
    if (!raw) {
      return '';
    }
    const base = raw.replace(/[^A-Za-z0-9]/g, '_');
    return `${base}_${IoMatrixWorkflowComponent.variantIdentifierSuffix(raw)}`;
  }

  private static variantIdentifierSuffix(rawToken: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < rawToken.length; i++) {
      hash ^= rawToken.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36).toUpperCase().padStart(7, '0').slice(-7);
  }

  private buildVariantCheckboxColumn(token: string): ColDef {
    const colId = `variant_${this.sanitizeVariantColId(token)}`;
    return {
      colId,
      headerName: token,
      editable: (params: any) => params?.data?.state !== 'deleted',
      cellDataType: 'boolean',
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      width: 130,
      minWidth: 110,
      valueGetter: params => this.rowHasVariantToken(params.data?.variant, token),
      valueSetter: params => {
        const row = params.data;
        if (!row) {
          return false;
        }
        this.applyVariantCheckboxToggle(row, token, !!params.newValue);
        return true;
      }
    };
  }

  protected onCreateIcdMetadataReset(): void {
    // Hook for child components to apply flow-specific defaults after metadata reset.
  }

  protected resetCreateIcdMetadata(): void {
    this.metadata = this.createDefaultMetadata();
    this.syncTemplateDerivedMetadata();
    this.onCreateIcdMetadataReset();
  }

  private createDefaultMetadata(): Record<string, string | boolean> {
    return {
      icdType: '',
      icdSubType: '',
      parentIcd: false,
      program: '',
      modelYear: '',
      rpo: '',
      additionalRpos: '',
      buildPhase: '',
      cadence: '',
      architecture: '',
      objectCode: '',
      objectCodeInst: '',
      vpps: '',
      upc: '',
      fna: '',
      deviceDre: '',
      electricalArchitect: '',
      delegateDeviceDre: '',
      delegateElectricalArchitect: '',
      deviceVse: '',
      electricalVse: '',
      deviceEgm: '',
      architectEgm: '',
      icdeEngineer: '',
      childObjectCodes: '',
      revision: '',
      securityClassifier: '',
      deviceVariant: '',
      deviceVariantDesc: '',
      buildStatus: '',
      platform: '',
      spLocation: '',
      diodeIFSM: '',
      diodeVRRM: '',
      operateNormally: '',
      fullyFunctional: '',
      icdSourced: '',
      icdSourcedDate: '',
      rcdInterfIdentifiedDate: todayAsIsoDate(),
      rcdConnDate: todayAsIsoDate(),
      rcdInterfAgreedDate: todayAsIsoDate(),
      rcdIverDate: todayAsIsoDate(),
      rcdSopDate: todayAsIsoDate(),
      delegateDeviceDreApprovalRequired: false,
      delegateElectricalArchitectApprovalRequired: false,
      allUsersEmailStatus: 'ON',
      allUsersEmail: '',
      icdeEngineerEmailStatus: 'ON',
      icdeEngineerEmail: '',
      deviceDreEmailStatus: 'ON',
      deviceDreEmail: '',
      electricalArchitectEmailStatus: 'ON',
      electricalArchitectEmail: '',
      deviceVseEmailStatus: 'ON',
      deviceVseEmail: '',
      electricalVseEmailStatus: 'ON',
      electricalVseEmail: '',
      deviceEgmEmailStatus: 'ON',
      deviceEgmEmail: '',
      architectEgmEmailStatus: 'ON',
      architectEgmEmail: ''
    };
  }

  private prepareIoMatrixRows(rows: IoMatrixRow[]): IoMatrixRow[] {
    return (rows || []).map((row: any) => {
      const providedOriginal = row?.originalRowData ? structuredClone(row.originalRowData) : undefined;
      const normalizedRow: any = {
        ...row,
        fieldRules: (row?.fieldRules && row.fieldRules.length)
          ? structuredClone(row.fieldRules)
          : structuredClone(IoMatrixWorkflowComponent.IO_MATRIX_FIELD_RULES),
        changes: row?.changes instanceof Map ? new Map<string, string>(row.changes) : new Map<string, string>(),
        state: row?.state || 'saved'
      };
      normalizedRow.originalRowData = providedOriginal || structuredClone({
        ...normalizedRow,
        changes: undefined,
        originalRowData: undefined
      });
      return normalizedRow;
    });
  }

  private extractErrorMessage(err: any, fallback: string): string {
    const errorPayload = err?.error;
    if (typeof errorPayload === 'string' && errorPayload.trim()) {
      return errorPayload;
    }
    if (errorPayload?.message) {
      return errorPayload.message;
    }
    if (err?.message) {
      return err.message;
    }
    return fallback;
  }

  private markIoMatrixRowsSaved(): void {
    const persistedRows = (this.ioMatrixRows || []).filter((row: any) => row?.state !== 'deleted');
    persistedRows.forEach((row: any) => {
      if (row?.changes?.clear) {
        row.changes.clear();
      } else {
        row.changes = new Map<string, string>();
      }
      row.state = 'saved';
      row.originalRowData = structuredClone({
        ...row,
        changes: undefined,
        originalRowData: undefined
      });
    });
    this.ioMatrixRows = persistedRows;
    this.templateRows = structuredClone(persistedRows);
    this.syncRowDataSignal();
    this.dataChanged = false;
    this.duplicate = false;
    this.rowsInvalid = false;
    if (this.isGridApiAlive(this.gridApi)) {
      this.gridApi.refreshCells({force: true});
      this.gridApi.redrawRows();
    }
  }

  protected scheduleFitGridWidth(): void {
    setTimeout(() => {
      if (!this.isGridApiAlive(this.gridApi)) {
        return;
      }
      const api: any = this.gridApi as any;
      if (typeof api.autoSizeAllColumns === 'function') {
        api.autoSizeAllColumns(false);
      }
      if (typeof api.sizeColumnsToFit === 'function') {
        api.sizeColumnsToFit();
      }
    }, 0);
  }
}

